import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import Popper from "@mui/base/PopperUnstyled";
import { Transition } from "react-transition-group";
import FocusLock from "react-focus-lock";
import uuid from "uuid/v4";
import classNames from "classnames";
import PlainButton from "../elements/plain-button";
import OutsideClick from "../elements/outside-click";
import useFocus from "../../hooks/use-focus";

const Popover2 = ({
  borderColor,
  wrapperClassName,
  contentWidth,
  trigger,
  children,
  disablePortal,
  showArrow = false,
  placement,
  contentClassName,
  disabled,
  onClose = () => {},
  onOpen = () => {},
  className = "",
  ...rest
}) => {
  const [anchorRef, setAnchorFocus] = useFocus();
  const [arrowRef, setArrow] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const is_open = Boolean(anchorEl);
  const uid = useRef(uuid());
  const id = is_open ? uid.current : undefined;

  const modifiers = [
    {
      name: "arrow",
      enabled: showArrow,
      options: {
        element: arrowRef,
      },
    },
    {
      name: "preventOverflow",
      enabled: true,
      options: {
        altAxis: true,
        padding: 16,
      },
    },
  ];

  const handleOpen = () => {
    setAnchorEl(anchorRef.current);
    onOpen();
  };

  const handleClose = (returnFocus = true) => {
    setAnchorEl(null);
    onClose();
    if (returnFocus) {
      setTimeout(() => {
        setAnchorFocus();
      }, 0);
    }
  };

  const onOutsideClick = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    handleClose(false);
  };

  useEffect(() => {
    const closeOnEsc = (e) => {
      if (is_open && e.key === "Escape") {
        handleClose();
      }
    };
    document.body.addEventListener("keydown", closeOnEsc, { capture: true });
    return () => {
      document.body.removeEventListener("keydown", closeOnEsc, { capture: true });
    };
  }, [is_open, handleClose]);

  const triggerEl =
    typeof trigger === "function" ? (
      React.cloneElement(
        trigger({
          is_open,
          openPopover: handleOpen,
          closePopover: handleClose,
          togglePopover: is_open ? handleClose : handleOpen,
        }),
        { ref: anchorRef, "aria-describedby": id }
      )
    ) : (
      <PlainButton ref={anchorRef} aria-describedby={id} onClick={is_open ? handleClose : handleOpen}>
        {trigger}
      </PlainButton>
    );

  return (
    <>
      {triggerEl}
      <Popper
        className={`et--popover2--outer ${className}`}
        modifiers={modifiers}
        id={id}
        open={is_open}
        anchorEl={anchorEl}
        disablePortal={disablePortal}
        placement={placement}
        {...rest}
      >
        {({ placement }) => (
          <>
            {showArrow ? <span className={"et--popover2--arrow " + placement} ref={(e) => setArrow(e)} /> : null}
            <OutsideClick onOutsideClick={onOutsideClick}>
              <div className={wrapperClassName} data-no-focus-lock>
                <Transition in={is_open} timeout={200} appear={true} unmountOnExit mountOnEnter>
                  {(state) => (
                    <FocusLock focusOptions={{ preventScroll: true }}>
                      <div
                        className={classNames("et--popover2", contentClassName, state)}
                        style={{ width: contentWidth }}
                      >
                        {typeof children === "function" ? children({ closePopover: handleClose }) : children}
                      </div>
                    </FocusLock>
                  )}
                </Transition>
              </div>
            </OutsideClick>
          </>
        )}
      </Popper>
    </>
  );
};

Popover2.propTypes = {
  disabled: PropTypes.bool,
  borderColor: PropTypes.string,
  children: PropTypes.any,
  contentClassName: PropTypes.string,
  contentWidth: PropTypes.number,
  disablePortal: PropTypes.bool,
  placement: PropTypes.string,
  showArrow: PropTypes.bool,
  trigger: PropTypes.any,
  wrapperClassName: PropTypes.any,
  onClose: PropTypes.func,
  onOpen: PropTypes.func,
  className: PropTypes.string,
};

export default Popover2;
